import { mediaUrl } from '@/lib/mediaUrl';
import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Check, CheckCircle2, Loader2, RotateCcw, Timer, PlayCircle } from 'lucide-react';

const QuestionRenderer = ({ question, index, answer, onChange, showResult, resultData }) => {
  const q = question;
  const isCorrect = resultData?.correct;

  // Synthesise True/False options if none provided
  const resolvedOptions = (() => {
    if (q.type === 'true_false' && (!q.options || q.options.length === 0)) {
      const correctAnswer = q.correct_answer;
      return [
        { id: 'true', text: 'True', value: 'true', is_correct: correctAnswer === true || correctAnswer === 'true' },
        { id: 'false', text: 'False', value: 'false', is_correct: correctAnswer === false || correctAnswer === 'false' },
      ];
    }
    return q.options || [];
  })();

  const getCorrectValue = () => {
    if (resultData?.correct_answer !== undefined && resultData?.correct_answer !== null) return resultData.correct_answer;
    if (q.correct_answer !== undefined && q.correct_answer !== null) return q.correct_answer;
    return null;
  };
  const correctValue = getCorrectValue();

  const isOptionCorrect = (opt, oi) => {
    // Check is_correct / isCorrect boolean first
    if (opt.is_correct === true || opt.isCorrect === true) return true;
    if (correctValue !== null) {
      const optVal = String(opt.value || opt.id || oi);
      return optVal.toLowerCase() === String(correctValue).toLowerCase();
    }
    return false;
  };

  const isMultipleCorrect = (opt, oi) => {
    if (opt.is_correct === true || opt.isCorrect === true) return true;
    const correctAnswers = resultData?.correct_answer || q.correct_answers || [];
    if (Array.isArray(correctAnswers) && correctAnswers.length > 0) return correctAnswers.map(String).includes(String(opt.value || opt.id || oi));
    return false;
  };

  return (
    <div className={`p-4 rounded-xl border ${showResult ? (isCorrect ? 'border-green-300 bg-green-50' : resultData?.requires_grading ? 'border-amber-300 bg-amber-50' : 'border-red-300 bg-red-50') : 'border-slate-200 bg-white'}`} data-testid={`question-${q.id}`}>
      <p className="font-medium text-slate-900 mb-3"><span className="text-[#20acf6] font-bold mr-2">Q{index + 1}.</span>{q.question_text}</p>
      {q.question_image_url && <img src={mediaUrl(q.question_image_url)} alt={q.question_image_alt || ""} className="rounded-lg mb-3 max-h-48 object-contain" />}
      {['single_choice', 'true_false', 'image_based', 'scenario_based'].includes(q.type) && (
        <div className="space-y-2">
          {q.scenario_text && <p className="text-sm text-slate-600 italic mb-3 p-3 bg-slate-50 rounded-lg">{q.scenario_text}</p>}
          {resolvedOptions.map((opt, oi) => {
            const optVal = String(opt.value || opt.id || oi);
            const isSelected = String(answer) === optVal;
            const optIsCorrect = isOptionCorrect(opt, oi);
            return (
              <label key={oi} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${showResult ? optIsCorrect ? 'border-green-400 bg-green-50 font-medium' : isSelected ? 'border-red-400 bg-red-50' : 'border-slate-200' : isSelected ? 'border-[#20acf6] bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`} data-testid={`option-${q.id}-${oi}`}>
                <input type="radio" name={`q-${q.id}`} value={optVal} checked={isSelected} onChange={(e) => onChange(e.target.value)} disabled={showResult} className="accent-[#20acf6]" />
                <span className="text-sm flex-1">{opt.text || opt.label || String(opt)}</span>
                {showResult && optIsCorrect && <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />}
                {showResult && isSelected && !optIsCorrect && <span className="text-red-500 text-xs shrink-0">Your answer</span>}
              </label>
            );
          })}
        </div>
      )}
      {q.type === 'multiple_choice' && (
        <div className="space-y-2">
          {(q.options || []).map((opt, oi) => {
            const val = String(opt.value || opt.id || oi);
            const selected = Array.isArray(answer) ? answer.includes(val) : false;
            const optIsCorrect = isMultipleCorrect(opt, oi);
            return (
              <label key={oi} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${showResult ? optIsCorrect ? 'border-green-400 bg-green-50 font-medium' : selected ? 'border-red-400 bg-red-50' : 'border-slate-200' : selected ? 'border-[#20acf6] bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`} data-testid={`option-${q.id}-${oi}`}>
                <input type="checkbox" checked={selected} onChange={() => { const prev = Array.isArray(answer) ? answer : []; onChange(selected ? prev.filter(v => v !== val) : [...prev, val]); }} disabled={showResult} className="accent-[#20acf6]" />
                <span className="text-sm flex-1">{opt.text || opt.label || String(opt)}</span>
                {showResult && optIsCorrect && <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />}
                {showResult && selected && !optIsCorrect && <span className="text-red-500 text-xs shrink-0">Incorrect</span>}
              </label>
            );
          })}
        </div>
      )}
      {q.type === 'fill_blank' && (
        <div className="space-y-2">
          <input type="text" value={answer || ''} onChange={(e) => onChange(e.target.value)} disabled={showResult && resultData?.correct} placeholder="Type your answer..." className={`w-full p-3 border rounded-lg text-sm focus:outline-none ${showResult ? (resultData?.correct ? 'border-green-400 bg-green-50' : 'border-red-400 bg-red-50') : 'border-slate-200 focus:border-[#20acf6]'}`} data-testid={`fill-blank-input-${q.id}`} />
          {showResult && resultData?.correct && <p className="text-sm text-green-700 font-medium">Correct! The answer is "{answer}".</p>}
          {showResult && !resultData?.correct && <p className="text-sm text-amber-700">That's not quite right. Try again on your next attempt.</p>}
        </div>
      )}
      {q.type === 'free_text' && (
        <div>
          <Textarea value={answer || ''} onChange={(e) => onChange(e.target.value)} disabled={showResult} placeholder="Write your answer..." rows={4} />
          {showResult && resultData?.requires_grading && <p className="text-xs text-amber-700 mt-2">Awaiting manual grading</p>}
        </div>
      )}
      {q.type === 'sequence_order' && <SequenceInput items={q.sequence || q.options?.map(o => o.text) || []} answer={answer} onChange={onChange} disabled={showResult} />}
      {q.type === 'match_pairs' && <p className="text-sm text-slate-500 italic">Match pairs: use the matching exercise element type for full interactivity.</p>}
      {showResult && q.explanation && <div className="mt-3 p-3 bg-blue-50 rounded-lg text-sm text-blue-800"><strong>Explanation:</strong> {q.explanation}</div>}
      {showResult && !isCorrect && !resultData?.requires_grading && (
        <div className="mt-2 p-2 bg-red-50 rounded-lg text-sm text-red-700 font-medium flex items-center gap-2" data-testid={`incorrect-feedback-${q.id}`}>
          <RotateCcw className="h-4 w-4 shrink-0" /> Incorrect — review the correct answer and try again.
        </div>
      )}
      {showResult && isCorrect && (
        <div className="mt-2 p-2 bg-green-50 rounded-lg text-sm text-green-700 font-medium flex items-center gap-2" data-testid={`correct-feedback-${q.id}`}>
          <CheckCircle2 className="h-4 w-4 shrink-0" /> Correct!
        </div>
      )}
    </div>
  );
};

const SequenceInput = ({ items, answer, onChange, disabled }) => {
  const [order, setOrder] = useState(answer || [...items]);
  const moveUp = (i) => { if (i === 0) return; const n = [...order]; [n[i - 1], n[i]] = [n[i], n[i - 1]]; setOrder(n); onChange(n); };
  const moveDown = (i) => { if (i >= order.length - 1) return; const n = [...order]; [n[i], n[i + 1]] = [n[i + 1], n[i]]; setOrder(n); onChange(n); };
  return (
    <div className="space-y-1">
      {order.map((item, i) => (
        <div key={`${item}-${i}`} className="flex items-center gap-2 p-2 bg-white border border-slate-200 rounded-lg">
          <span className="text-xs text-slate-400 w-5">{i + 1}.</span>
          <span className="flex-1 text-sm">{item}</span>
          {!disabled && <div className="flex gap-1"><button onClick={() => moveUp(i)} className="p-1 hover:bg-slate-100 rounded" disabled={i === 0}>&#9650;</button><button onClick={() => moveDown(i)} className="p-1 hover:bg-slate-100 rounded" disabled={i >= order.length - 1}>&#9660;</button></div>}
        </div>
      ))}
    </div>
  );
};

const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);

const QuizTimer = ({ secondsLeft }) => {
  const m = Math.floor(secondsLeft / 60);
  const sec = String(secondsLeft % 60).padStart(2, '0');
  return (
    <div className={`sticky top-0 z-10 flex justify-end`} data-testid="quiz-timer">
      <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-mono border shadow-sm ${secondsLeft <= 60 ? 'bg-red-50 text-red-700 border-red-200' : 'bg-white text-slate-700 border-slate-200'}`}>
        <Timer className="h-4 w-4" /> {m}:{sec}
      </span>
    </div>
  );
};

const QuizElement = ({ config, element, progress, onSubmitQuiz, isCompleted, currentModuleIdx, onModuleRestart }) => {
  const questions = config.questions || [];
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showExplanations, setShowExplanations] = useState(false);
  const [moduleRestarting, setModuleRestarting] = useState(false);
  const [attemptKey, setAttemptKey] = useState(0);
  const [started, setStarted] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(null);

  const { api } = useAuth();
  const [extraTime, setExtraTime] = useState('none');
  const baseLimit = Number(config.time_limit_minutes) || 0;
  const timedQuiz = config.time_limit_enabled === true && baseLimit > 0;
  useEffect(() => {
    if (!timedQuiz) return;
    api.get('/player/my-accommodations').then(r => setExtraTime(r.data?.extra_time || 'none')).catch(() => {});
  }, [timedQuiz, api]);
  const timerOn = timedQuiz && extraTime !== 'unlimited';
  const extraPct = Number(extraTime) || 0;
  const timeLimit = Math.round(baseLimit * (1 + extraPct / 100));

  const displayQuestions = useMemo(() => {
    const qs = config.shuffle_questions !== false ? shuffle(questions) : questions;
    if (!config.shuffle_answers) return qs;
    return qs.map(q => (['single_choice', 'multiple_choice', 'image_based', 'scenario_based'].includes(q.type) && q.options?.length)
      ? { ...q, options: shuffle(q.options) } : q);
  }, [attemptKey, element?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const passingScore = config.passing_score || 70;
  const maxFailedAttempts = Number(config.max_attempts) || 3;
  const prevSubmissions = (progress?.quiz_submissions || []).filter(s => s.element_id === element.id);
  const failedAttempts = prevSubmissions.filter(s => !s.passed).length;
  const attemptsRemaining = maxFailedAttempts - failedAttempts;

  const answersRef = useRef(answers);
  answersRef.current = answers;

  const submit = useCallback(async (force = false) => {
    const current = answersRef.current;
    const requiredQs = questions.filter(q => q.type !== 'free_text');
    const unanswered = requiredQs.filter(q => { const a = current[q.id]; if (a === undefined || a === null || a === '') return true; if (Array.isArray(a) && a.length === 0) return true; return false; });
    if (!force && unanswered.length > 0) { toast.error(`Please answer all ${requiredQs.length} questions before submitting. ${unanswered.length} unanswered.`); return; }
    setSubmitting(true);
    try {
      const res = await onSubmitQuiz(current);
      setResult(res);
      setSecondsLeft(null);
      setShowExplanations(config.show_explanation !== false);
      if (!res.passed) {
        const totalFails = failedAttempts + 1;
        if (totalFails >= maxFailedAttempts) { setModuleRestarting(true); toast.error(`You've used all ${maxFailedAttempts} attempts. The entire module will restart so you can review the material.`); setTimeout(() => onModuleRestart(currentModuleIdx), 3000); }
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to submit quiz'); }
    finally { setSubmitting(false); }
  }, [questions, onSubmitQuiz, config.show_explanation, failedAttempts, maxFailedAttempts, onModuleRestart, currentModuleIdx]);

  const handleSubmit = () => submit(false);
  const submitRef = useRef(submit);
  submitRef.current = submit;

  useEffect(() => {
    if (secondsLeft === null || result) return undefined;
    if (secondsLeft <= 0) { toast.info("Time's up — your answers have been submitted."); submitRef.current(true); return undefined; }
    const t = setTimeout(() => setSecondsLeft(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secondsLeft, result]);

  const startQuiz = () => { setStarted(true); if (timerOn) setSecondsLeft(timeLimit * 60); };

  const handleRetry = () => { setAnswers({}); setResult(null); setShowExplanations(false); setAttemptKey(k => k + 1); setStarted(false); setSecondsLeft(null); };

  if (questions.length === 0) return <p className="text-slate-500 italic">No questions configured for this quiz.</p>;
  if (moduleRestarting) return (
    <div className="p-8 text-center space-y-4 bg-red-50 border border-red-200 rounded-xl" data-testid="module-restart-notice">
      <RotateCcw className="h-10 w-10 text-red-500 mx-auto animate-spin" />
      <h3 className="text-lg font-bold text-red-800">Module Restarting</h3>
      <p className="text-sm text-red-700">You've reached the maximum of {maxFailedAttempts} failed attempts. The entire module will restart so you can review all the content before trying again.</p>
    </div>
  );

  if (isCompleted && !result) return (
    <div className="p-6 rounded-xl bg-green-50 border border-green-200 space-y-3" data-testid="quiz-already-passed">
      <h3 className="text-lg font-bold text-green-800 flex items-center gap-2"><CheckCircle2 className="h-5 w-5" /> You've passed this quiz</h3>
      {prevSubmissions.length > 0 && <p className="text-sm text-green-800">Best score: {Math.max(...prevSubmissions.map(a => a.score || 0))}%</p>}
      <p className="text-sm text-green-700">You can continue to the next activity.</p>
    </div>
  );

  if (timerOn && !started && !result) return (
    <div className="p-6 rounded-xl border border-slate-200 bg-white space-y-4" data-testid="quiz-start-screen">
      <h3 className="text-lg font-bold text-[#2a3e5d]">{config.is_final_assessment ? 'Final Assessment' : 'Quiz'}</h3>
      <ul className="text-sm text-slate-600 space-y-1">
        <li>{questions.length} questions · pass mark {passingScore}%</li>
        <li className="flex items-center gap-1.5"><Timer className="h-4 w-4" /> Time limit: {timeLimit} minutes. The timer starts when you click Start Quiz.</li>
        {extraPct > 0 && <li className="text-[#20acf6] font-medium" data-testid="quiz-extra-time-note">Includes your extra time (+{extraPct}%).</li>}
      </ul>
      <Button onClick={startQuiz} disabled={attemptsRemaining <= 0} className="bg-[#20acf6] hover:bg-[#3bb8f7] min-h-[44px]" data-testid="start-quiz-btn">
        <PlayCircle className="h-4 w-4 mr-2" /> Start Quiz
      </Button>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="quiz-element">
      {timerOn && secondsLeft !== null && !result && <QuizTimer secondsLeft={secondsLeft} />}
      <div className={`p-3 rounded-lg text-sm ${attemptsRemaining <= 1 ? 'bg-red-50 text-red-800 border border-red-200' : 'bg-blue-50 text-blue-800'}`} data-testid="quiz-attempts-info">
        <div className="flex items-center justify-between"><span>Pass mark: <strong>{passingScore}%</strong></span><span>{failedAttempts > 0 ? <>Failed attempts: <strong>{failedAttempts}/{maxFailedAttempts}</strong></> : <>{maxFailedAttempts} attempts allowed</>}</span></div>
        {attemptsRemaining === 1 && !result && <p className="mt-1 text-xs font-medium text-red-700">Warning: This is your last attempt. If you fail, the entire module will restart.</p>}
        {prevSubmissions.length > 0 && <p className="mt-1 text-xs">Best score: {Math.max(...prevSubmissions.map(a => a.score || 0))}%</p>}
      </div>
      {result ? (
        <div className={`p-6 rounded-xl ${result.passed ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`} data-testid="quiz-result-panel">
          <h3 className={`text-lg font-bold ${result.passed ? 'text-green-800' : 'text-red-800'}`}>{result.passed ? 'Congratulations! You passed!' : 'Not quite there yet'}</h3>
          <p className="mt-2 text-sm">Score: <strong>{result.score}%</strong> ({result.earned_points}/{result.total_points} points)</p>
          <p className="text-sm">Pass mark: {passingScore}%</p>
          {!result.passed && attemptsRemaining > 0 && (
            <div className="mt-4 p-3 bg-white/60 rounded-lg border border-red-100"><p className="text-sm text-red-700 mb-3">Review the answers below, then retry when you're ready.</p><Button onClick={handleRetry} className="bg-[#20acf6] hover:bg-[#3bb8f7] text-white" data-testid="quiz-retry-btn"><RotateCcw className="h-4 w-4 mr-2" /> Retry Quiz ({attemptsRemaining} attempt{attemptsRemaining !== 1 ? 's' : ''} remaining)</Button></div>
          )}
          {!result.passed && attemptsRemaining <= 0 && <p className="mt-3 text-sm text-red-700 font-medium">No attempts remaining. The module will restart so you can review the material.</p>}
        </div>
      ) : null}
      {!result && displayQuestions.map((q, qi) => <QuestionRenderer key={q.id || qi} question={q} index={qi} answer={answers[q.id]} onChange={(val) => setAnswers(prev => ({ ...prev, [q.id]: val }))} showResult={false} />)}
      {result && showExplanations && displayQuestions.map((q, qi) => <QuestionRenderer key={q.id || qi} question={q} index={qi} answer={result.answers?.[q.id]?.answer} showResult={true} resultData={result.answers?.[q.id]} onChange={() => {}} />)}
      {result && !result.passed && attemptsRemaining > 0 && showExplanations && (
        <div className="sticky bottom-0 bg-white/90 backdrop-blur-sm border-t border-slate-200 py-3 -mx-1 px-1 z-[55]"><Button onClick={handleRetry} className="w-full bg-[#20acf6] hover:bg-[#3bb8f7] text-white h-11" data-testid="quiz-retry-bottom-btn"><RotateCcw className="h-4 w-4 mr-2" /> Retry Quiz ({attemptsRemaining} attempt{attemptsRemaining !== 1 ? 's' : ''} remaining)</Button></div>
      )}
      {!result && <Button onClick={handleSubmit} disabled={submitting || attemptsRemaining <= 0} className="bg-[#20acf6] hover:bg-[#3bb8f7] w-full sm:w-auto" data-testid="submit-quiz-btn">{submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Submit Answers</Button>}
    </div>
  );
};

export default QuizElement;
